import { Injectable } from '@angular/core';
import { SettingsService } from '../firestore/settings.service';

/**
 * Service responsible for managing the Chrome Extension
 */
@Injectable({
  providedIn: 'root'
})
export class ChromeExtensionService {

  /**
   * Indicates if the extension is turned on or off
   */
  turnOn: boolean = false;

  scriptInjectedOnActiveTab: boolean = false;

  /**
   * Object containing the current settings of the extension
   */
  currentSettings = {
    turnOnState: false,
    //enableFloatIcon: false,
    enableCtrlV: false,
    enableCtrlI: true,
    enableCtrlY: true,
    enableAltY: true,
    enableSaveOnEnter:false
  };

  takeNoteShortcut = '';
  takeScreenshotShortcut = '';
  takeScreenshotAndSaveShortcut = '';
  pasteShortcut = '';

  /**
   * Stores activated tabs domains with expiration dates
  */
  activatedTabsDomains: { tabId: number, domain: string, expireDate: number }[] = [];

  /**
   * Constructor which initializes the current settings by calling the `getStorageData` method
   */
  constructor(private settingsService: SettingsService) {
    if (this.settingsService.isChromeExtension())
      this.initialize();

  const isMac = this.getOperatingSystem() === 'mac';
  this.takeNoteShortcut = isMac ? '(⌘ + I)' : '(Ctrl + I)';
  this.takeScreenshotShortcut = isMac ? '(⌘ + Y)' : '(Ctrl + Y)';
  this.takeScreenshotAndSaveShortcut = isMac ? '(Option + Y)' : '(Alt + Y)';
  this.pasteShortcut = isMac ? '(⌘ + V)' : '(Ctrl + V)';

  }

  private getOperatingSystem(): 'mac' | 'windows' | 'linux' | 'unknown' {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mac')) return 'mac';
    if (ua.includes('win')) return 'windows';
    if (ua.includes('linux')) return 'linux';
    return 'unknown';
  }

  private async initialize() {
    try {
      await this.getStorageData();
      await this.loadActivatedTabsDomains();

      // checa se o script está no tab atual
      chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
        if (tabs[0]) {

          const url = tabs[0].url || "";

          // 🔥 Verifica se a URL é de uma página do sistema (chrome://, edge://, about:, etc.)
          if (!url || url.startsWith("chrome") || url.startsWith("chrome-extension://") || url.startsWith("chrome://") || url.startsWith("edge://") || url.startsWith("about:")) {
            console.log("❌ Página do sistema detectada, não podemos injetar script:", url);
            this.scriptInjectedOnActiveTab = true; // 🔥 Marca como já injetado para evitar erro
            return;
          }

          chrome.tabs.sendMessage(tabs[0].id, { action: 'checkStatus' }, (response) => {
            if (chrome.runtime.lastError) {
              // Caso o script ainda não esteja injetado
              //console.warn('Content script not injected:', chrome.runtime.lastError.message);
              //this.turnOn = false;
              this.scriptInjectedOnActiveTab = false;
            } else if (response) {
              // O script foi injetado e respondeu
              this.turnOn = response.turnOnState;
              this.scriptInjectedOnActiveTab = true;
            } else {
              // Caso o script não responda por outro motivo
              console.warn('No response received from content script');
              //this.turnOn = false;
              this.scriptInjectedOnActiveTab = false;
            }

            if (!this.scriptInjectedOnActiveTab && this.currentSettings.turnOnState) {
              // vai injetar e ativar
              this.turnOnActions();
            }
          });
        }
      });
    } catch (error) {
      console.error('Error getting storage data:', error);
    }


  }

  /**
   * Loads activated tabs domains from storage
   */
  private loadActivatedTabsDomains() {
    return new Promise<void>((resolve) => {
      chrome.storage.local.get(['activatedTabsDomains'], (result) => {
        this.activatedTabsDomains = result.activatedTabsDomains || [];
        console.log("🔄 activatedTabsDomains loaded:", this.activatedTabsDomains);

        // Remove expired entries
        const now = Date.now();
        this.activatedTabsDomains = this.activatedTabsDomains.filter(entry => entry.expireDate > now);

        // Save updated list without expired entries
        chrome.storage.local.set({ activatedTabsDomains: this.activatedTabsDomains }, resolve);
      });
    });
  }

   /**
   * Updates activated tabs domains in local storage
   */
   private updateActivatedTabs(tabId: number, url: string) {

    let domain: string;

    try {
        domain = new URL(url).hostname; // 🔥 Extrai o hostname se for uma URL válida
    } catch (error) {
        console.warn("❌ URL inválida, ignorando:", url);
        return; // 🚨 Se a URL for inválida, não adiciona à lista
    }

    const expireDate = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days

    // Remove expired entries
    this.activatedTabsDomains = this.activatedTabsDomains.filter(entry => entry.expireDate > Date.now());

    // Check if tab already exists in the list
    const existingIndex = this.activatedTabsDomains.findIndex(entry => entry.tabId === tabId);

    if (existingIndex !== -1) {
      this.activatedTabsDomains[existingIndex] = { tabId, domain, expireDate };
    } else {
      this.activatedTabsDomains.push({ tabId, domain, expireDate });
    }

    // Save updated list
    chrome.storage.local.set({ activatedTabsDomains: this.activatedTabsDomains }, () => {
      console.log("✅ Updated activatedTabsDomains:", this.activatedTabsDomains);
    });
  }

  /**
   * Turns on the extension
   */
  private turnOnActions() {

    if (this.scriptInjectedOnActiveTab) {
      this.turnOn = true;
      return;
    }

    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      if (tabs[0]) {
        if (!this.scriptInjectedOnActiveTab) {
          // Inject CSS first
          chrome.scripting.insertCSS({
            target: { tabId: tabs[0].id, allFrames: true },
            files: ['foreground_styles.css']
          }, () => {
            // Inject foreground.js after CSS
            chrome.scripting.executeScript({
              target: { tabId: tabs[0].id, allFrames: true },
              files: ['foreground.js']
            }, () => {
              // Send a message to enable the floating button
              //chrome.tabs.sendMessage(tabs[0].id, { action: 'toggleButton', status: true });
              this.turnOn = true;
              this.scriptInjectedOnActiveTab = true; // Update flag
              this.updateActivatedTabs(tabs[0].id, tabs[0].url); // Store the active tab domain
            });
          });
        } else {
          // Toggle the floating button directly

          //nao precisa mandar mensagem, vai pegar pela mudança do storage
          //chrome.tabs.sendMessage(tabs[0].id, { action: 'toggleButton', status: true });

          this.turnOn = true;
        }
      }
    });
  }

  /**
   * Turns off the extension
   */
  private turnOffActions() {

    this.turnOn = false;

    // nao precisa mandar mensagem, vai pegar pela mudança do storage
    /*
    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      if (tabs[0]) {
          // Toggle the floating button directly
          chrome.tabs.sendMessage(tabs[0].id, { action: 'toggleButton', status: false });
          this.turnOn = false;
      }
    });
    */
  }

  /**
   * Retrieves the extension's settings from the storage
   * @returns Promise<any> - Promise that resolves to the extension's settings
   */
  public getStorageDataService():Promise<any> {
    return new Promise((resolve, reject) => {
      chrome.storage.local.get('settings',  ({settings}) => {
        console.log(settings + ' tem o tipo de ' + typeof(settings)); //data
        resolve(settings); {isActive:true}
      });
    });
  }

  /**
   * Retrieves the extension's settings from the storage and updates the `currentSettings` object
   * @returns Promise<any> - Promise that resolves to void
   */
  async getStorageData(): Promise<any> {

    try {
      let  settings = await this.getStorageDataService();

      this.currentSettings = settings;

      console.log(this.currentSettings); //{isActive:true};

      this.turnOn = this.currentSettings.turnOnState;

    } catch (error) {
      // inital

      this.currentSettings = {
        turnOnState: false,
        //enableFloatIcon: true,
        enableCtrlV: false,
        enableCtrlI: true,
        enableCtrlY: true,
        enableAltY: true,
        enableSaveOnEnter:false
      };

      this.turnOn = this.currentSettings.turnOnState;
    }
    //if you want you can return a value and access it with then() function in the ngOnInit
  }

  /**
   * Updates the `turnOnState` property of the `currentSettings` object and saves the settings to the storage
   * @param value - boolean indicating if the extension should be turned on or off
   */
  turnON(value: boolean) {
    this.currentSettings.turnOnState = value;

    this.updateCurrentSettingsOnLocalStorage();


    if (value) {
      this.turnOnActions();
    } else {
      this.turnOffActions();
    }
  }

  updateCurrentSettingsOnLocalStorage(turnOnToggle: boolean = false) {
    chrome.storage.local.set({ settings: this.currentSettings }, () => {
      if (chrome.runtime.lastError) {
        console.error('Erro ao salvar as configurações:', chrome.runtime.lastError);
      } else {
        console.log('Configurações salvas com sucesso!');
      }
    });

    if (turnOnToggle) {
      this.currentSettings.turnOnState ? this.turnOnActions() : this.turnOffActions();
    }
  }


}

